/**
 * Athlete ↔ organization membership predicate.
 *
 * Single source of truth for "does this athlete belong to this organization?",
 * resolved through the `athlete_organizations` join table.
 *
 * Lives in its own leaf module (importing only the Supabase client + logger) so
 * that BOTH `organizationScope.ts` (used by `canUserAccessAthlete`) and
 * `athleteCrossover.ts` can depend on it without creating an import cycle between
 * those two modules. `organizationScope.ts` re-exports this so existing
 * `@/utils/supabase/organizationScope` import paths are unchanged. Extracted
 * verbatim from `organizationScope.ts` — no behavior change.
 */

import { supabase } from './client';
import { createLogger } from '@/utils/logger/devLogger';

// Keep the original 'organizationScope' log namespace so this helper's log
// output is byte-identical to before the extraction (pure refactor).
const logger = createLogger('organizationScope');

/**
 * Checks if an athlete belongs to an organization
 */
export async function isAthleteInOrganization(athleteId: string, organizationId: string): Promise<boolean> {
  try {
    // UUID validation regex pattern
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    // Check if the ID is a valid UUID format
    if (!uuidPattern.test(athleteId)) {
      logger.warn(`Invalid athlete ID format: ${athleteId} is not a valid UUID`);
      return false;
    }

    // Try direct Supabase query first
    const { data, error } = await supabase
      .from('athlete_organizations')
      .select('athlete_id')
      .eq('athlete_id', athleteId)
      .eq('organization_id', organizationId)
      .maybeSingle();

    if (error) {
      logger.warn('Direct query failed in isAthleteInOrganization, assuming false:', error);
      // Return false instead of throwing error to prevent infinite loops
      return false;
    }

    return !!data;
  } catch (error) {
    logger.warn('Exception in isAthleteInOrganization, assuming false:', error);
    // Return false instead of throwing error to prevent infinite loops
    return false;
  }
}
